import type { MutationFunctionContext } from '@tanstack/react-query';
import { Effect } from 'effect';
import { type AppRuntime, useAppRuntime } from '../runtime-context';

// Cache-side optimistic lifecycle for the canonical TanStack Query
// optimistic-update pattern
// (docs/cloned-repos-as-docs/query/docs/framework/react/guides/optimistic-updates.md):
//   onMutate   – awaits cancelQueries → snapshots → writes optimistic state →
//                returns snapshot
//   onError    – restores snapshot
//   onSettled  – invalidates the list
//
// Two flavors:
//   - `buildOptimisticLifecycle({ runtime, ... })` — pure factory taking the
//     runtime as a parameter. Used by unit tests so the sequence can be
//     exercised without React.
//   - `useOptimisticLifecycle({ ... })` — hook that pulls the runtime from
//     `AppRuntimeContext`. Used by production hooks.
//
// Each callback body is an `Effect` program run via `runtime.runPromise(...)`,
// so the cancelQueries step is properly awaited — an in-flight refetch can't
// land after the optimistic write and clobber it.

type LifecycleOptions<Row, V> = {
  queryKey: readonly unknown[];
  optimistic: (rows: ReadonlyArray<Row>, v: V) => ReadonlyArray<Row>;
};

type LifecycleCallbacks<Row, V> = {
  onMutate: (
    v: V,
    ctx: MutationFunctionContext,
  ) => Promise<{ snapshot: ReadonlyArray<Row> }>;
  onError: (
    err: unknown,
    v: V,
    onMutateResult: { snapshot: ReadonlyArray<Row> } | undefined,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
  onSettled: (
    data: unknown,
    err: unknown,
    v: V,
    onMutateResult: unknown,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
};

export function buildOptimisticLifecycle<Row, V>(
  opts: LifecycleOptions<Row, V> & { runtime: AppRuntime },
): LifecycleCallbacks<Row, V> {
  const { queryKey, optimistic, runtime } = opts;
  return {
    onMutate: (v, ctx) =>
      runtime.runPromise(
        Effect.gen(function* () {
          yield* Effect.promise(() => ctx.client.cancelQueries({ queryKey }));
          const snapshot =
            ctx.client.getQueryData<ReadonlyArray<Row>>(queryKey) ?? [];
          ctx.client.setQueryData<ReadonlyArray<Row>>(queryKey, (rows) =>
            optimistic(rows ?? [], v),
          );
          return { snapshot };
        }),
      ),
    onError: (_err, _v, onMutateResult, ctx) =>
      runtime.runPromise(
        Effect.sync(() => {
          if (onMutateResult) {
            ctx.client.setQueryData(queryKey, onMutateResult.snapshot);
          }
        }),
      ),
    onSettled: (_d, _e, _v, _r, ctx) =>
      runtime.runPromise(
        Effect.promise(() => ctx.client.invalidateQueries({ queryKey })),
      ),
  };
}

export function useOptimisticLifecycle<Row, V>(
  opts: LifecycleOptions<Row, V>,
): LifecycleCallbacks<Row, V> {
  const runtime = useAppRuntime();
  // No memo: callers pass `opts` as a fresh object literal each render, so a
  // `useMemo([runtime, opts])` would rebuild every render anyway. The callbacks
  // are spread into `useMutation` (which always reads the latest) so a stable
  // identity buys nothing here.
  return buildOptimisticLifecycle({ ...opts, runtime });
}
